import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Page, PageHeader, Empty } from "@/components/shared";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarCheck } from "lucide-react";
import { YE_STEPS, fmtWhen, defaultYearEndYear, yearEndYearOptions } from "@/components/YearEndChecklist";

function Stat({ label, value }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <p className="label-caps">{label}</p>
      <p className="font-display text-2xl mt-2">{value}</p>
    </div>
  );
}

export default function YearEnd() {
  const [year, setYear] = useState(defaultYearEndYear());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    setData(null);
    api.get(`/year-end/overview?year=${year}`).then((r) => setData(r.data)).catch(() => {});
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (cid, key) => {
    const row = data.customers.find((c) => c.customer_id === cid);
    const cur = row?.steps?.[key]?.done;
    const r = await api.put(`/customers/${cid}/year-end`, { year, step: key, done: !cur });
    setData((prev) => {
      const customers = prev.customers.map((c) =>
        c.customer_id === cid
          ? { ...c, steps: r.data.steps, done_count: r.data.done_count, total: r.data.total, complete: r.data.complete }
          : c
      );
      const by_step = {};
      YE_STEPS.forEach((s) => { by_step[s.key] = 0; });
      let fully = 0;
      customers.forEach((c) => {
        YE_STEPS.forEach((s) => { if (c.steps?.[s.key]?.done && c.steps?.[s.key]?.available !== false) by_step[s.key] += 1; });
        if (c.complete) fully += 1;
      });
      return { ...prev, customers, totals: { ...prev.totals, fully_complete: fully, by_step } };
    });
  };

  return (
    <Page>
      <PageHeader title="Årsregnskap" subtitle="Fremdrift for årsoppgjør per kunde med årsregnskapsprodukt" testid="yearend-header">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-32 bg-secondary border-border" data-testid="yearend-year-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            {yearEndYearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      {!data ? (
        <p className="text-muted-foreground">Laster…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="yearend-totals">
            <Stat label="Årsregnskapskunder" value={data.totals.customers} />
            <Stat label="Ferdig komplett" value={`${data.totals.fully_complete} / ${data.totals.customers}`} />
            <Stat label="Steg per kunde" value={data.totals.total_steps} />
            <Stat label="Regnskapsår" value={data.year} />
          </div>

          <div className="bg-card border border-border rounded-xl p-5 mt-4">
            <p className="label-caps mb-3">Fremdrift per steg</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {YE_STEPS.map((s) => {
                const n = data.totals.by_step[s.key] || 0;
                const total = (data.totals.available_by_step || {})[s.key] || 0;
                const pct = total ? Math.round((n / total) * 100) : 0;
                return (
                  <div key={s.key} title={s.desc} data-testid={`yearend-progress-${s.key}`}>
                    <p className="text-xs text-muted-foreground truncate">{s.label}</p>
                    <p className="font-display text-lg mt-1">{n}/{total}</p>
                    <div className="h-1.5 bg-secondary rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {data.customers.length === 0 ? (
            <div className="mt-6">
              <Empty icon={CalendarCheck} title="Ingen kunder med årsregnskap" hint="Kunder som har produktet «Årsregnskap» vises her." />
            </div>
          ) : (
            <div className="mt-6 overflow-x-auto bg-card border border-border rounded-xl">
              <table className="w-full text-sm min-w-[1000px]">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-4 py-3 label-caps sticky left-0 bg-card z-10">Kunde</th>
                    <th className="px-3 py-3 label-caps text-center">Fremdrift</th>
                    {YE_STEPS.map((s) => (
                      <th key={s.key} className="px-3 py-3 label-caps text-center" title={s.desc}>{s.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.customers.map((c) => (
                    <tr key={c.customer_id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`yearend-row-${c.customer_id}`}>
                      <td className="px-4 py-3 sticky left-0 bg-card">
                        <p className="font-medium">{c.customer_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {c.org_number || "—"}{c.engagement_manager_name ? ` · ${c.engagement_manager_name}` : ""}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${c.complete ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}`}>
                          {c.done_count}/{c.total}
                        </span>
                      </td>
                      {YE_STEPS.map((s) => {
                        const st = c.steps?.[s.key] || {};
                        if (st.available === false) {
                          return (
                            <td key={s.key} className="px-3 py-3 text-center align-middle" data-testid={`yearend-step-${c.customer_id}-${s.key}`} title="Ikke bestilt">
                              <span className="text-muted-foreground/40 text-xs">–</span>
                            </td>
                          );
                        }
                        return (
                          <td key={s.key} className="px-3 py-3 text-center align-top">
                            <div className="flex flex-col items-center gap-1">
                              <Checkbox checked={!!st.done} onCheckedChange={() => toggle(c.customer_id, s.key)} data-testid={`yearend-step-${c.customer_id}-${s.key}`} />
                              {st.done && st.by && (
                                <span className="text-[10px] text-muted-foreground leading-tight max-w-[90px]">
                                  {st.by}<br />{fmtWhen(st.at)}
                                </span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Page>
  );
}
